import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
	AuthFlowProvider,
	AUTH_MODES,
} from '../../../services/Contexts/AuthFlowContext';
import AuthContainer from '../../../components/Auth/SignInReg/AuthContainer';
import { AuthBrandingProvider } from '../../../services/Contexts/AuthBrandingContext';
import { useAuthenticatedUserInfo } from '../../../services/Contexts/AuthenticatedUserInfoContext';
import { getPortalSubdomain } from '../../../utils/subdomainUtils';
import { getBranding } from '../../../services/brandingService';
import { captureAppSumoParamsFromSearch } from '../../../utils/appsumoSession';
import { captureUtmFromSearch } from '../../../utils/utmAttribution';
import {
	buildCrossAppUrl,
	getSystemAdminAppHomePath,
	getSystemFrontendUrl,
	resolveCrossAppRoleRedirectUrl,
} from '../../../utils/crossAppUrls';
import { resolveAuthModeFromLocation } from '../../../utils/authModeUrl';
import { usePublicBrandColor } from '../../../services/Contexts/BrandThemeContext';

const SignInSignUpPage = () => {
	const { userId, token, userRole } = useAuthenticatedUserInfo();
	const [branding, setBranding] = useState({});
	usePublicBrandColor(branding?.primaryColor);
	const location = useLocation();
	const navigate = useNavigate();
	const currentSubdomain = getPortalSubdomain(window.location.hostname);
	const forceLogin = Boolean(currentSubdomain);
	const initialMode = forceLogin
		? AUTH_MODES.SIGN_IN
		: resolveAuthModeFromLocation({
				pathname: location.pathname,
				search: location.search,
			});

	useEffect(() => {
		captureAppSumoParamsFromSearch(location.search);
		captureUtmFromSearch(location.search);
	}, [location.search]);

	useEffect(() => {
		if (forceLogin && location.pathname === '/signup') {
			navigate('/auth', { replace: true });
		}
	}, [forceLogin, location.pathname, navigate]);

	useEffect(() => {
		const subdomain = getPortalSubdomain(window.location.hostname);
		if (!subdomain) {
			setBranding({});
			return;
		}

		let isMounted = true;
		getBranding(subdomain)
			.then((response) => {
				if (isMounted) {
					setBranding(response.data);
				}
			})
			.catch(() => {
				if (isMounted) {
					setBranding({});
				}
			});

		return () => {
			isMounted = false;
		};
	}, []);

	useEffect(() => {
		if (!userId || !token || userRole !== 'System Admin') return;
		const redirectUrl =
			resolveCrossAppRoleRedirectUrl({
				userRole,
				token,
				pathname: location.pathname,
				search: location.search,
			}) ||
			buildCrossAppUrl(
				getSystemFrontendUrl(),
				getSystemAdminAppHomePath(),
				token,
			);
		if (redirectUrl) {
			window.location.replace(redirectUrl);
		}
	}, [userId, token, userRole, location.pathname, location.search]);

	if (userId && token && userRole) {
		if (userRole === 'System Admin') {
			return null;
		}
		switch (userRole) {
			case 'Company Admin':
				return <Navigate to='/dashboard/company' replace />;
			case 'Employee':
				return <Navigate to='/dashboard/employee' replace />;
			default:
				return <Navigate to='/dashboard/company' replace />;
		}
	}

	return (
		<AuthFlowProvider initialMode={initialMode} syncUrl={!forceLogin}>
			<AuthBrandingProvider branding={branding}>
				<AuthContainer />
			</AuthBrandingProvider>
		</AuthFlowProvider>
	);
};

export default SignInSignUpPage;
